import { Stack, Typography, useTheme } from "@mui/material";
import { Drawer, InfoIcon } from "components";
import { useContext } from "react";

import {
  DoYouNeedCustomisedPlan,
  RadioItem,
  SelectDesiredIntegrations,
  SelectMissingFeatures,
  SelectReasonForNotUsing,
} from "./components";
import {
  FeedbackContext,
  FeedbackProvider,
  PrimaryCancellationReasonValue,
} from "./context";
import { useCancelSubscription } from "controller";
import toast from "react-hot-toast";
import { addMixpanelEvent } from "utilz/helpers";

export const CancelSubscriptionFeedbackDrawerContent = ({
  currentAccessBlockDate,
}: {
  currentAccessBlockDate: string;
}) => {
  const theme = useTheme();
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error("wrong context provided");
  }
  const { primaryReason, setPrimaryReason, buildPayload, resetForm } = context;
  const cancellationReasonsList = [
    {
      id: "price_too_high",
      label: "The price is too high to justify",
      actionItem: <DoYouNeedCustomisedPlan />,
    },
    {
      id: "desired_integrations_unavailable",
      label: "The integration I am looking for is not available",
      actionItem: <SelectDesiredIntegrations />,
    },
    {
      id: "missing_features",
      label: "Missing features I needed",
      actionItem: <SelectMissingFeatures />,
    },
    {
      id: "not_using_anymore",
      label: "I am not using DeskDay anymore",
      actionItem: <SelectReasonForNotUsing />,
    },
  ];
  return (
    <Stack p="24px" gap="40px" minHeight="100%">
      <Stack gap="12px">
        <Stack
          direction="row"
          alignItems={"flex-start"}
          gap="8px"
          border={`1px solid ${theme.palette.error.main}`}
          borderRadius="8px"
          p="14px"
          bgcolor={theme.palette.error.tint}
        >
          <InfoIcon
            style={{
              width: "24px",
              height: "24px",
            }}
            color="error"
          />
          <Stack gap="4px">
            <Typography variant="h6" color={"#CE293A"}>
              Sorry to see you go!
            </Typography>
            <Typography variant="body-medium" color={"#CE293A"}>
              You'll lose access to all DeskDay features. We've kept the door
              open till {currentAccessBlockDate}.
            </Typography>
          </Stack>
        </Stack>
        <Typography variant="body-medium" color={theme.palette.lightBg.low}>
          We’d love to know how we can improve. Your feedback helps us serve you
          better.
        </Typography>
      </Stack>

      {/* choose reason section */}
      <Stack gap="12px">
        <Typography variant="body-large">
          What is the primary reason you're considering leaving?
        </Typography>
        {cancellationReasonsList.map((reason) => (
          <RadioItem
            key={reason.id}
            label={reason.label}
            actionItem={reason.actionItem}
            onChange={() =>
              setPrimaryReason({
                value: reason.id as PrimaryCancellationReasonValue,
                label: reason.label,
              })
            }
            isSelected={primaryReason?.value === reason.id}
          />
        ))}
      </Stack>
    </Stack>
  );
};

export const CancelSubscriptionFeedbackDrawerWithContext = ({
  open,
  onClose,
  onConfirmClick,
  currentAccessBlockDate,
}: {
  open: boolean;
  onClose: () => void;
  onConfirmClick: () => void;
  currentAccessBlockDate: string;
}) => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error("wrong context provided");
  }
  const { isPrimaryButtonDisabled } = context;

  const {
    mutateAsync: cancelSubscription,
    isLoading: isCancelSubscriptionLoading,
  } = useCancelSubscription();

  const handleClose = () => {
    onClose();
    context?.resetForm();
  };

  const handleSubmit = async () => {
    if (!context) return;
    const payload = context.buildPayload();
    if (!payload) {
      return;
    }
    try {
      await cancelSubscription(payload);
      toast.success("Your subscription is cancelled!");
      addMixpanelEvent({
        event: "subscription_cancelled",
        data: payload,
      });
      onConfirmClick && onConfirmClick();
      handleClose();
    } catch (error) {
      toast.error("Failed to cancel subscription.");
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Cancel subscription"
      drawerStyle={{
        width: "628px",
      }}
      primaryButtonText="Submit"
      secondaryButtonText={"Close"}
      onPrimaryButtonClick={handleSubmit}
      onSecondaryButtonClick={handleClose}
      isLoading={isCancelSubscriptionLoading}
      isPrimaryButtonDisabled={isPrimaryButtonDisabled}
    >
      <CancelSubscriptionFeedbackDrawerContent
        currentAccessBlockDate={currentAccessBlockDate}
      />
    </Drawer>
  );
};

export const CancelSubscriptionFeedbackDrawer = ({
  open,
  onClose,
  onConfirmClick,
  currentAccessBlockDate,
}: {
  open: boolean;
  onClose: () => void;
  onConfirmClick: () => void;
  currentAccessBlockDate: string;
}) => {
  return (
    <FeedbackProvider>
      <CancelSubscriptionFeedbackDrawerWithContext
        open={open}
        onClose={onClose}
        onConfirmClick={onConfirmClick}
        currentAccessBlockDate={currentAccessBlockDate}
      />
    </FeedbackProvider>
  );
};
